import { FC, useEffect, useId, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { cn } from '@fe-learner-common';
import { publishAnalyticsEvent } from '@fe-core-events';

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;
const loader = new Loader({
  apiKey: API_KEY,
  version: 'weekly',
});

const getCoordinates = async (location: string) => {
  try {
    const geocoder = new google.maps.Geocoder();
    const { results } = await geocoder.geocode({ address: location });
    return results?.[0].geometry.location;
  } catch (e) {
    return null;
  }
};

async function initMap(location: string, root: HTMLElement) {
  const { Map } = (await google.maps.importLibrary(
    'maps',
  )) as google.maps.MapsLibrary;

  const { AdvancedMarkerElement } = (await google.maps.importLibrary(
    'marker',
  )) as google.maps.MarkerLibrary;

  const coordinates = await getCoordinates(location);
  if (!coordinates) {
    return null;
  }

  const mapOptions = {
    center: coordinates,
    zoom: 16,
    disableDefaultUI: true,
    clickableIcons: false,
    keyboardShortcuts: false,
    draggable: false,
    mapId: 'fe-learner-courses-map',
  };
  const map = new Map(root, mapOptions);
  new AdvancedMarkerElement({
    map,
    position: coordinates,
  });

  return map;
}

type MapProps = { className?: string; location: string };

export const Map: FC<MapProps> = ({ className, location }) => {
  const [map, setMap] = useState<google.maps.Map | null>();
  const uniqueId = useId();

  useEffect(() => {
    const root = document.getElementById(uniqueId);
    async function loadMap(_root: HTMLElement) {
      await loader.load();
      const newMap = await initMap(location, _root);
      setMap(newMap);

      publishAnalyticsEvent({
        application: 'fe-learner',
        track: {
          event: {
            context: 'google-maps-js-api-loader',
            action: 'map-loaded',
          },
        },
      });
    }

    if (location && root && !map) {
      loadMap(root);
    }
  }, [location, map]);

  return (
    <div
      aria-hidden={true}
      tabIndex={-1}
      className={cn('h-[200px]', className)}
      id={uniqueId}
    />
  );
};
